import { buildQueryParams } from "../utils/buildQueryParams";
import { APIInstance } from "./api.config";

const { api } = new APIInstance({ path: "/v1/crm/pro/" });

const buildQuery = (filters: Record<string, any> = {}) => {
  const params = buildQueryParams(filters);
  return params.toString();
};

export const LeadService = {
  // Leads --------------------------------------------------------------
  getAllLeads: async (filters: Record<string, any> = {}) => {
    const { data } = await api.get(`/leads?${buildQuery(filters)}`);
    return data?.rows ?? [];
  },

  getAllLeadsWithCount: async (filters: Record<string, any> = {}) => {
    const { data } = await api.get(`/leads?${buildQuery(filters)}`);
    return data; // { rows, count }
  },

  getLeadById: async (id: number) => {
    const { data } = await api.get(`/leads/${id}`);
    return data;
  },

  createLead: async (leadData: any) => {
    const { data } = await api.post("/leads", leadData);
    return data;
  },

  updateLead: async (id: number, updatedLead: any) => {
    const { data } = await api.patch(`/leads/${id}`, updatedLead);
    return data;
  },

  // Composite endpoint — the only way priority is changed. Server always logs the activity.
  trackLeadActivity: async (id: number, payload: { priorityId?: number; note?: string }) => {
    const { data } = await api.patch(`/leads/${id}/activity`, payload);
    return data;
  },

  deleteLead: async (id: number) => {
    const { data } = await api.delete(`/leads/${id}`);
    return data;
  },

  getLeadActivities: async (leadId: number): Promise<any[]> => {
    const { data } = await api.get(`/leads/${leadId}/activities?${buildQuery({ limit: 100 })}`);
    return data?.rows ?? [];
  },

  getLeadDocuments: async (leadId: number): Promise<any[]> => {
    const { data } = await api.get(`/leads/${leadId}/documents`);
    return data ?? [];
  },

  uploadLeadDocument: async (leadId: number, formData: FormData) => {
    const { data } = await api.post(`/leads/${leadId}/documents`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },

  deleteLeadDocument: async (leadId: number, attachmentId: number) => {
    const { data } = await api.delete(`/leads/${leadId}/documents/${attachmentId}`);
    return data;
  },

  // Tasks ----------------------------------------------------------------
  getAllLeadTasks: async (filters: Record<string, any> = {}): Promise<any[]> => {
    const { data } = await api.get(`/tasks?${buildQuery({ limit: 1000, ...filters })}`);
    return data?.rows ?? [];
  },

  getLeadTasks: async (leadId: number): Promise<any[]> => {
    const { data } = await api.get(`/tasks?${buildQuery({ leadId, limit: 1000 })}`);
    return data?.rows ?? [];
  },

  getTaskById: async (id: number) => {
    const { data } = await api.get(`/tasks/${id}`);
    return data;
  },

  createTask: async (taskData: any) => {
    const { data } = await api.post("/tasks", taskData);
    return data;
  },

  updateTask: async (id: number, updatedTask: any) => {
    const { data } = await api.patch(`/tasks/${id}`, updatedTask);
    return data;
  },

  deleteTask: async (id: number) => {
    const { data } = await api.delete(`/tasks/${id}`);
    return data;
  },

  // Composite endpoint — logs progress, transitions status, optionally attaches a file, all in one call
  addTaskProgress: async (taskId: number, formData: FormData) => {
    const { data } = await api.post(`/tasks/${taskId}/progress`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },

  getTaskProgress: async (taskId: number): Promise<any[]> => {
    const { data } = await api.get(`/tasks/${taskId}/progress?${buildQuery({ limit: 100 })}`);
    return data?.rows ?? [];
  },
};
